import * as React from 'react';
import { cn } from '../lib/cn';
import { UniversalPageHeader, type UniversalPageHeaderProps } from './UniversalPageHeader';
import { UniversalTabs, type UniversalTabItem } from './UniversalTabs';

/**
 * UniversalPage — single source of page chrome.
 *
 * Combines the page header, an optional tab row, and a vertical-rhythm
 * content wrapper into one frame so every page in the portal lands with
 * the same spacing and structure. Use this as the outermost element of
 * every authenticated page; never re-implement header + spacing by hand.
 *
 * Layout (top → bottom):
 *   1. UniversalPageHeader  (breadcrumbs · title · description · meta · actions)
 *   2. UniversalTabs        (only when `tabs` is supplied)
 *   3. <div class="space-y-6">{children}</div>   — 24 px between every section
 *
 * For record-scoped detail pages (e.g. Service detail, Domain detail), pass
 * `tabs` to render the route-aware horizontal tabs and use `<Outlet />` in
 * `children` to render the active tab. Pass `tabsRenderOutlet={false}` if
 * you supply your own Outlet below the children area.
 *
 * Example — flat page:
 *   <UniversalPage
 *     title="Services"
 *     description="Hosting subscriptions and addons."
 *     breadcrumbs={[{ label: 'Home', href: '/app' }, { label: 'Services' }]}
 *     actions={<Button>Order new</Button>}
 *   >
 *     <UniversalTable {...} />
 *   </UniversalPage>
 *
 * Example — record-scoped tabs:
 *   <UniversalPage
 *     title={service.primaryDomain}
 *     meta={<StatusBadge>...</StatusBadge>}
 *     tabs={[
 *       { label: 'Overview', to: '.', end: true },
 *       { label: 'Limits',   to: 'limits' },
 *       ...
 *     ]}
 *   />
 *   // The `<Outlet />` is rendered automatically inside the tabs.
 */

export interface UniversalPageProps extends UniversalPageHeaderProps {
  /** Optional record-scoped tab items. When supplied, renders UniversalTabs
   *  between the header and the content area. */
  tabs?: UniversalTabItem[];
  /** Right-side actions shown beside the tab list (Edit / Print etc.).
   *  Distinct from `actions` which sits in the page header. */
  tabActions?: React.ReactNode;
  /** Tab visual style — defaults to 'underline'. */
  tabsVariant?: 'underline' | 'pill';
  /** When tabs are present, render `<Outlet />` beneath them automatically.
   *  Default `true`. Pass `false` if `children` already includes its own Outlet
   *  or non-tab content that should appear under the tab row. */
  tabsRenderOutlet?: boolean;
  /** Page content. Each direct child becomes a 24 px-spaced section. */
  children?: React.ReactNode;
  /** Override the content-area class (e.g. tighten or remove rhythm). */
  contentClassName?: string;
}

/**
 * UniversalPage — see file-level JSDoc above for usage and examples.
 */
export function UniversalPage({
  title,
  description,
  breadcrumbs,
  backTo,
  actions,
  meta,
  tabs,
  tabActions,
  tabsVariant = 'underline',
  tabsRenderOutlet = true,
  children,
  className,
  contentClassName,
}: UniversalPageProps) {
  const hasTabs = tabs && tabs.length > 0;
  return (
    <>
      <UniversalPageHeader
        title={title}
        description={description}
        breadcrumbs={breadcrumbs}
        backTo={backTo}
        actions={actions}
        meta={meta}
        className={className}
      />
      {hasTabs && (
        <UniversalTabs
          items={tabs}
          actions={tabActions}
          variant={tabsVariant}
          withOutlet={tabsRenderOutlet}
        />
      )}
      {children !== undefined && (
        <div className={cn('space-y-6', contentClassName)}>{children}</div>
      )}
    </>
  );
}
